import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { DataCard, SectionHeader, StatusPill, PriorityPill, EmptyState } from "../components/UI";
import Avatar from "../components/Avatar";
import { TID } from "../constants/testIds";
import {
  Plus, ArrowRight, Sparkles, FileText, ShieldCheck, BookOpen, CheckCircle2,
  Clock, MessageSquare, ScrollText, Bell, TrendingUp, AlertTriangle, PenLine,
  LifeBuoy, Zap, Calendar,
} from "lucide-react";

const KIND_META = {
  policy_signature: { icon: PenLine, tint: "bg-amber-50 text-amber-800 border-amber-200", cta: "Read & sign" },
  case_reply_due: { icon: MessageSquare, tint: "bg-blue-50 text-blue-800 border-blue-200", cta: "Reply" },
  case_assigned: { icon: FileText, tint: "bg-violet-50 text-violet-800 border-violet-200", cta: "Open" },
  findings_approval: { icon: ShieldCheck, tint: "bg-rose-50 text-rose-800 border-rose-200", cta: "Review" },
  ticket_reply_due: { icon: LifeBuoy, tint: "bg-emerald-50 text-emerald-800 border-emerald-200", cta: "Reply" },
};

const PRIO_RANK = { critical: 0, high: 1, medium: 2, low: 3 };

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [cases, setCases] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [highlights, setHighlights] = useState({ trending_policies: [], attention_policies: [] });
  const [myAcks, setMyAcks] = useState([]);

  useEffect(() => {
    api.get("/analytics/overview").then(r => setOverview(r.data)).catch(() => {});
    api.get("/cases", { params: { limit: 6 } }).then(r => setCases(r.data)).catch(() => {});
    api.get("/analytics/my-tasks").then(r => setTasks(r.data.items || [])).catch(() => {});
    api.get("/analytics/dashboard-highlights").then(r => setHighlights(r.data || {})).catch(() => {});
    api.get("/my/policy-acknowledgments").then(r => setMyAcks(r.data || [])).catch(() => {});
  }, []);

  const firstName = (user.name || "there").split(" ")[0];
  const dueSoonTasks = tasks.slice(0, 5);
  const signedCount = myAcks.length;

  return (
    <div className="pb-12" data-testid="employee-dashboard">
      {/* Hero */}
      <div className="border-b border-slate-200 bg-gradient-to-br from-blue-50/60 via-white to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Avatar user={user} size={56} testid="dashboard-avatar"/>
              <div>
                <div className="flex items-center gap-2 text-[10px] font-mono tracking-widest text-slate-500 uppercase mb-1">
                  <Zap size={12} strokeWidth={2}/> {greetingByHour()} · {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
                </div>
                <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 leading-tight">
                  Welcome back, {firstName}.
                </h1>
                <div className="text-slate-500 text-sm mt-1">Your PropHR workspace , report concerns, track cases, and stay on top of what needs you.</div>
              </div>
            </div>
            <Link to="/cases/new" className="btn-primary" data-testid={TID.navCreate}>
              <Plus size={16} strokeWidth={2}/> Report a case
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-8 space-y-8">
        {/* Snapshot stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <DataCard label="My cases" value={overview?.total ?? "·"} testid="stat-my-total"/>
          <DataCard label="Open" value={overview?.open ?? "·"} testid="stat-my-open" accent/>
          <DataCard label="Resolved" value={overview?.resolved ?? "·"} testid="stat-my-resolved"/>
          <DataCard label="Signed policies" value={signedCount} testid="stat-signed-policies" sublabel="ALL TIME"/>
        </div>

        {/* Row: tasks (2/3) + insight (1/3) */}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Your to-do list */}
            <section className="data-cell p-6" data-testid="dashboard-tasks">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-amber-50 border border-amber-200 flex items-center justify-center">
                    <CheckCircle2 size={14} strokeWidth={1.75} className="text-amber-700"/>
                  </div>
                  <div>
                    <div className="font-display text-lg font-semibold text-slate-900">Your to-do list</div>
                    <div className="text-xs text-slate-500">Items waiting for your response.</div>
                  </div>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
                  {tasks.length} pending
                </span>
              </div>
              {dueSoonTasks.length === 0 ? (
                <div className="border border-dashed border-slate-200 bg-slate-50/50 p-6 text-center">
                  <CheckCircle2 size={22} strokeWidth={1.5} className="mx-auto text-emerald-600 mb-2"/>
                  <div className="text-sm font-medium text-slate-900">You&apos;re all caught up.</div>
                  <div className="text-xs text-slate-500 mt-1">No pending actions right now , enjoy the calm.</div>
                </div>
              ) : (
                <div className="space-y-2">
                  {dueSoonTasks.map((t, i) => {
                    const meta = KIND_META[t.kind] || KIND_META.case_assigned;
                    const Icon = meta.icon;
                    return (
                      <Link
                        key={i}
                        to={t.href}
                        className="flex items-center gap-3 p-3 border border-slate-200 hover:border-slate-400 hover:bg-slate-50 transition-colors group"
                        data-testid={`task-${t.kind}-${i}`}
                      >
                        <div className={"w-8 h-8 flex items-center justify-center border shrink-0 " + meta.tint}>
                          <Icon size={14} strokeWidth={1.75}/>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{t.title}</div>
                          <div className="text-[11px] text-slate-500 truncate">{t.subtitle}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {t.priority && t.priority !== "medium" && <PriorityPill priority={t.priority}/>}
                          <span className="text-xs text-slate-600 group-hover:text-slate-900 inline-flex items-center gap-1">
                            {meta.cta} <ArrowRight size={11} strokeWidth={2} className="group-hover:translate-x-0.5 transition-transform"/>
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                  {tasks.length > 5 && (
                    <div className="text-center pt-2">
                      <span className="text-xs text-slate-500 font-mono uppercase tracking-widest">
                        +{tasks.length - 5} more · complete these first
                      </span>
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Recent Cases with dates */}
            <section className="data-cell p-6" data-testid="dashboard-recent-cases">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-50 border border-blue-200 flex items-center justify-center">
                    <FileText size={14} strokeWidth={1.75} className="text-blue-700"/>
                  </div>
                  <div>
                    <div className="font-display text-lg font-semibold text-slate-900">Recent cases</div>
                    <div className="text-xs text-slate-500">Your latest workspace activity.</div>
                  </div>
                </div>
                <Link to="/cases" className="text-xs text-blue-700 hover:underline flex items-center gap-1">
                  View all <ArrowRight size={12}/>
                </Link>
              </div>
              {cases.length === 0 ? (
                <EmptyState
                  icon={FileText}
                  title="You have no cases yet"
                  subtitle="Have a workplace concern? Report it and get help , anonymously if you prefer."
                  action={<Link to="/cases/new" className="btn-primary"><Plus size={16}/> Report a case</Link>}
                />
              ) : (
                <div className="space-y-2">
                  {cases.map((c) => (
                    <Link
                      key={c.id}
                      to={`/cases/${c.id}`}
                      className="flex items-center gap-3 px-4 py-3 border border-slate-200 hover:border-slate-400 hover:bg-slate-50 transition-colors"
                      data-testid={`recent-case-${c.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate mb-0.5">{c.title}</div>
                        <div className="flex items-center gap-2 text-[11px] text-slate-500 flex-wrap">
                          <span className="font-mono">{c.case_number}</span>
                          <span>·</span>
                          <span>{c.category}</span>
                          <span>·</span>
                          <span className="inline-flex items-center gap-1"><Calendar size={10} strokeWidth={2}/>{new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
                          <span className="hidden md:inline">·</span>
                          <span className="hidden md:inline text-slate-400">{timeAgo(c.updated_at || c.created_at)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <PriorityPill priority={c.priority}/>
                        <StatusPill status={c.status}/>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>
          </div>

          {/* Right column , confidential-by-design + privacy */}
          <div className="space-y-6">
            <section className="ai-surface tracing-beam p-6" data-testid="dashboard-confidential">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={16} strokeWidth={1.75} className="text-blue-400"/>
                <span className="eyebrow text-blue-300">Confidential by design</span>
              </div>
              <div className="text-slate-100 leading-relaxed font-editorial text-lg mb-4">
                &ldquo;Every case opens with a trust envelope. Anonymous reports never expose your identity to your manager. Voice notes are transcribed automatically so you never have to type long descriptions.&rdquo;
              </div>
              <div className="border-t border-slate-800 pt-3 text-xs text-slate-400 leading-relaxed">
                Retaliation against a reporter is a policy violation and is permanently recorded in the audit trail.
              </div>
            </section>

            <section className="data-cell p-6" data-testid="dashboard-rights">
              <div className="eyebrow mb-3 flex items-center gap-2">
                <ShieldCheck size={12} strokeWidth={2}/> Your privacy & rights
              </div>
              <ul className="text-sm text-slate-700 space-y-2.5">
                <li className="flex gap-2"><span className="text-emerald-600 mt-0.5">✓</span> Reports are protected by SOC 2 Type II controls.</li>
                <li className="flex gap-2"><span className="text-emerald-600 mt-0.5">✓</span> Anonymous cases hide your identity from HR &amp; managers.</li>
                <li className="flex gap-2"><span className="text-emerald-600 mt-0.5">✓</span> Retaliation against reporters is prohibited.</li>
                <li className="flex gap-2"><span className="text-emerald-600 mt-0.5">✓</span> You may withdraw or amend your case any time.</li>
              </ul>
              <Link to="/help" className="mt-4 text-xs text-blue-700 hover:underline inline-flex items-center gap-1">
                Learn more <ArrowRight size={11}/>
              </Link>
            </section>
          </div>
        </div>

        {/* Policies row , trending + attention */}
        <section data-testid="dashboard-policies-row">
          <div className="flex items-center gap-2 mb-3">
            <ScrollText size={16} className="text-slate-700" strokeWidth={2}/>
            <div className="eyebrow">Policies</div>
          </div>
          <SectionHeader
            title="Policies to know & sign"
            subtitle="Trending across your workspace and freshly-published policies that need attention."
            right={<Link to="/policies" className="text-xs text-blue-700 hover:underline flex items-center gap-1">View library <ArrowRight size={12}/></Link>}
          />
          <div className="grid md:grid-cols-2 gap-6">
            <PolicyLane
              title="Trending"
              subtitle="Most-signed policies across the workspace."
              Icon={TrendingUp}
              tint="text-emerald-700 bg-emerald-50 border-emerald-200"
              policies={highlights.trending_policies || []}
              empty="No signature activity yet."
              renderMeta={(p) => `${p.signature_count} signature${p.signature_count === 1 ? "" : "s"}`}
            />
            <PolicyLane
              title="Needs attention"
              subtitle="Recently published policies with low sign-rate."
              Icon={AlertTriangle}
              tint="text-amber-700 bg-amber-50 border-amber-200"
              policies={highlights.attention_policies || []}
              empty="No policies need attention right now."
              renderMeta={(p) => `${p.signature_count} signature${p.signature_count === 1 ? "" : "s"} · new`}
            />
          </div>
        </section>

        {/* Quick actions strip */}
        <section className="grid md:grid-cols-3 gap-4" data-testid="dashboard-quick-actions">
          <QuickAction
            to="/cases/new"
            Icon={Plus}
            title="Report a case"
            body="Voice notes, anonymity, and legal signatures are one click away."
            tint="from-blue-50 to-white"
          />
          <QuickAction
            to="/help"
            Icon={BookOpen}
            title="Help center"
            body="Step-by-step guides, FAQs, and escalation paths."
            tint="from-emerald-50 to-white"
          />
          <QuickAction
            to="/tickets"
            Icon={LifeBuoy}
            title="Contact support"
            body="Ask PropHR support a question or report a bug."
            tint="from-violet-50 to-white"
          />
        </section>
      </div>
    </div>
  );
}

function greetingByHour() {
  const h = new Date().getHours();
  if (h < 5) return "Late tonight";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Working late";
}

function PolicyLane({ title, subtitle, Icon, tint, policies, empty, renderMeta }) {
  return (
    <div className="data-cell p-5" data-testid={`policy-lane-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-start gap-3 mb-4">
        <div className={"w-9 h-9 border flex items-center justify-center shrink-0 " + tint}>
          <Icon size={16} strokeWidth={1.75}/>
        </div>
        <div>
          <div className="font-display text-base font-semibold text-slate-900">{title}</div>
          <div className="text-xs text-slate-500">{subtitle}</div>
        </div>
      </div>
      {policies.length === 0 ? (
        <div className="text-xs text-slate-500 italic px-3 py-6 text-center border border-dashed border-slate-200 bg-slate-50/40">
          {empty}
        </div>
      ) : (
        <div className="space-y-2">
          {policies.map((p) => (
            <Link
              key={p.id}
              to={`/policies/${p.id}`}
              className="flex items-start gap-3 border border-slate-200 p-3 hover:border-slate-400 transition-colors group"
            >
              <ScrollText size={14} strokeWidth={1.75} className="text-slate-500 mt-0.5 shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate group-hover:text-blue-700 transition-colors">{p.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mt-0.5">
                  {p.category} · {renderMeta(p)}
                </div>
              </div>
              <ArrowRight size={12} strokeWidth={2} className="text-slate-400 group-hover:translate-x-0.5 transition-transform mt-1"/>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function QuickAction({ to, Icon, title, body, tint }) {
  return (
    <Link
      to={to}
      className={"group border border-slate-200 bg-gradient-to-br " + tint + " p-5 hover:border-slate-400 transition-colors"}
      data-testid={`quick-${title.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} strokeWidth={1.75} className="text-slate-700"/>
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Quick action</span>
      </div>
      <div className="font-display text-base font-semibold text-slate-900 mb-1">{title}</div>
      <div className="text-sm text-slate-600 leading-relaxed">{body}</div>
      <div className="mt-3 text-xs text-slate-700 group-hover:text-blue-700 transition-colors inline-flex items-center gap-1">
        Open <ArrowRight size={11} strokeWidth={2} className="group-hover:translate-x-0.5 transition-transform"/>
      </div>
    </Link>
  );
}
